import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import {
  SparklesIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  LockClosedIcon,
  ArrowRightOnRectangleIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import './FirstLoginModal.css';

const FirstLoginModal = () => {
  const { currentUser, updateCurrentUser, logout } = useAuth();
  const [fullName, setFullName] = useState(currentUser?.full_name || '');
  const [phone, setPhone] = useState(currentUser?.phone || '');
  const [personalEmail, setPersonalEmail] = useState(currentUser?.personal_email || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (currentUser) {
      if (currentUser.full_name) setFullName(currentUser.full_name);
      if (currentUser.phone) setPhone(currentUser.phone);
      if (currentUser.personal_email) setPersonalEmail(currentUser.personal_email);
    }
  }, [currentUser]);

  // Show if user is logged in and first_login_completed is false
  if (closed || !currentUser || currentUser.first_login_completed) {
    return null;
  }

  const roleLower = (currentUser.role || '').toLowerCase();
  const isStudent = roleLower === 'student';
  const isAdmin = ['admin', 'administrator'].includes(roleLower);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setError('Vui lòng nhập họ và tên.');
      return;
    }

    if (isStudent && !phone.trim()) {
      setError('Vui lòng nhập số điện thoại để trung tâm liên hệ và cấp chứng chỉ.');
      return;
    }

    // For admin/staff, new password is required on first login
    if (!isStudent && !newPassword.trim()) {
      setError('Vui lòng đặt mật khẩu mới cho lần đăng nhập đầu tiên.');
      return;
    }

    if (newPassword && newPassword.length < 6) {
      setError('Mật khẩu mới phải có tối thiểu 6 ký tự.');
      return;
    }

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      setError('Mật khẩu xác nhận không trùng khớp.');
      return;
    }

    if (newPassword && !confirmPassword) {
      setError('Vui lòng nhập lại mật khẩu mới để xác nhận.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const updatedUser = await userService.completeProfile({
        full_name: fullName.trim(),
        phone: phone.trim() || undefined,
        personal_email: personalEmail.trim() || undefined,
        new_password: newPassword.trim() || undefined,
      });

      setSuccess(true);
      if (updatedUser) {
        updateCurrentUser(updatedUser);
      }
      setTimeout(() => {
        setClosed(true);
      }, 600);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Không thể cập nhật thông tin. Vui lòng thử lại.');
      setSaving(false);
    }
  };

  return (
    <div className="first-login-overlay" role="dialog" aria-modal="true">
      <div className="first-login-card">
        <div className="first-login-header">
          <div className="first-login-badge">
            {isAdmin ? (
              <>
                <ShieldCheckIcon className="first-login-icon" />
                <span>Bảo mật tài khoản Quản trị</span>
              </>
            ) : (
              <>
                <SparklesIcon className="first-login-icon" />
                <span>Kích hoạt tài khoản lần đầu</span>
              </>
            )}
          </div>
          <h2>
            {isAdmin
              ? 'Thiết lập mật khẩu lần đầu'
              : 'Chào mừng bạn đến với Sao Việt Edu'}
          </h2>
          <p>
            {isAdmin
              ? 'Tài khoản quản trị của bạn vừa được cấp mật khẩu ngẫu nhiên. Để đảm bảo an toàn hệ thống, vui lòng đổi sang mật khẩu mới trước khi tiếp tục.'
              : 'Tài khoản của bạn được cấp ngẫu nhiên. Vui lòng điền thông tin chính xác để hệ thống lưu hồ sơ, lịch sử học tập và kết quả bài thi.'}
          </p>
        </div>

        {error && (
          <div className="first-login-alert error">
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </div>
        )}

        {success && (
          <div className="first-login-alert success">
            <CheckCircleIcon className="w-5 h-5 inline mr-1" />
            {isAdmin
              ? 'Đổi mật khẩu thành công! Đang chuyển hướng...'
              : 'Kích hoạt thành công! Đang chuyển hướng vào cổng học tập...'}
          </div>
        )}

        <form onSubmit={handleSubmit} className="first-login-form" autoComplete="off">
          {/* Dummy hidden inputs to absorb browser autofill */}
          <div style={{ position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden', zIndex: -1 }}>
            <input type="text" name="fake_username_remembered" tabIndex={-1} autoComplete="off" />
            <input type="password" name="fake_password_remembered" tabIndex={-1} autoComplete="new-password" />
          </div>

          <div className="first-login-field">
            <label>Tài khoản đăng nhập</label>
            <input
              type="text"
              value={currentUser.email || currentUser.username || ''}
              disabled
              className="first-login-input disabled"
            />
            <span className="first-login-hint">Tên tài khoản dùng để đăng nhập hệ thống.</span>
          </div>

          <div className="first-login-field">
            <label>
              {isStudent ? 'Họ và tên học viên' : 'Họ và tên người dùng'} <span className="req">*</span>
            </label>
            <div className="first-login-input-wrap">
              <UserIcon className="first-login-field-icon" />
              <input
                type="text"
                name="user_full_name"
                autoComplete="off"
                required
                autoFocus
                placeholder={isStudent ? 'Ví dụ: Nguyễn Văn An' : 'Ví dụ: Phạm Khải'}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="first-login-input"
              />
            </div>
          </div>

          <div className="first-login-field">
            <label>
              Số điện thoại liên hệ {isStudent && <span className="req">*</span>}
            </label>
            <div className="first-login-input-wrap">
              <PhoneIcon className="first-login-field-icon" />
              <input
                type="tel"
                name="user_phone"
                autoComplete="off"
                required={isStudent}
                placeholder="Ví dụ: 0912 345 678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="first-login-input"
              />
            </div>
            {isStudent && (
              <span className="first-login-hint">Dùng để giáo viên liên hệ và xác nhận kết quả kiểm tra.</span>
            )}
          </div>

          <div className="first-login-field">
            <label>Email cá nhân (tùy chọn)</label>
            <div className="first-login-input-wrap">
              <EnvelopeIcon className="first-login-field-icon" />
              <input
                type="text"
                inputMode="email"
                name="user_personal_email_custom"
                id="user_personal_email_custom"
                autoComplete="off"
                placeholder="email@gmail.com"
                value={personalEmail}
                onChange={(e) => setPersonalEmail(e.target.value)}
                className="first-login-input"
              />
            </div>
          </div>

          <div className="first-login-field">
            <label>
              {isStudent ? 'Đổi mật khẩu mới (tùy chọn)' : 'Mật khẩu mới'} {!isStudent && <span className="req">*</span>}
            </label>
            <div className="first-login-input-wrap">
              <LockClosedIcon className="first-login-field-icon" />
              <input
                type="password"
                name="user_new_password_custom"
                id="user_new_password_custom"
                autoComplete="new-password"
                minLength={6}
                required={!isStudent}
                placeholder={isStudent ? 'Để trống nếu muốn giữ nguyên mật khẩu được cấp' : 'Nhập mật khẩu mới (tối thiểu 6 ký tự)'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="first-login-input"
              />
            </div>
          </div>

          {(!isStudent || newPassword) && (
            <div className="first-login-field">
              <label>
                Xác nhận mật khẩu mới <span className="req">*</span>
              </label>
              <div className="first-login-input-wrap">
                <LockClosedIcon className="first-login-field-icon" />
                <input
                  type="password"
                  name="user_confirm_password_custom"
                  id="user_confirm_password_custom"
                  autoComplete="new-password"
                  minLength={6}
                  required
                  placeholder="Nhập lại mật khẩu mới vừa đặt"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="first-login-input"
                />
              </div>
            </div>
          )}

          <div className="first-login-actions">
            <button
              type="submit"
              disabled={saving || success}
              className="first-login-submit-btn"
            >
              {success
                ? 'Đã cập nhật thành công ✓'
                : saving
                ? 'Đang cập nhật...'
                : isAdmin
                ? 'Đổi mật khẩu & Vào hệ thống 🚀'
                : isStudent
                ? 'Xác nhận & Vào học ngay 🚀'
                : 'Xác nhận & Tiếp tục 🚀'}
            </button>
            <button
              type="button"
              onClick={logout}
              className="first-login-logout-btn"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4 inline mr-1" />
              Đăng xuất
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FirstLoginModal;
